/**
 * React Login Page Component
 *
 * Matches the styling and layout of the AngularJS login page template
 * from TECAuth/templates/registration/login.html
 *
 * Features:
 * - Session-based authentication with Django backend
 * - SMS two-factor authentication (2FA) via Twilio
 * - Remember me functionality
 * - Password reset link
 * - Exact styling match with AngularJS version
 * - HIPAA-compliant 2FA with 5-minute code expiration
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './LoginPage.css';

interface LoginPageProps {
  next?: string;
  singleUser?: boolean;
  logo?: string;
  version?: string;
  deployedEnv?: string;
}

interface LoginFormData {
  username: string;
  password: string;
  rememberMe: boolean;
  twoFactorCode?: string;
}

interface LoginError {
  field?: string;
  message: string;
}

const LoginPage: React.FC<LoginPageProps> = ({
  next: nextProp = '/',
  singleUser,
  logo,
  version,
  deployedEnv
}) => {
  // Get config from window.appContext if not provided via props
  const appContext = (window as any).appContext || {};
  const actualSingleUser = singleUser !== undefined ? singleUser : (appContext.singleUser || false);
  const actualLogo = logo || appContext.logo || '/static/imgs/tecdoc-logo.png.webp';
  const actualLogoUrl = appContext.logoUrl || null; // Tenant-specific logo URL
  const actualVersion = version || appContext.version || '';
  const actualDeployedEnv = deployedEnv || appContext.deployedEnv || '';

  // Extract 'next' parameter from URL query string (e.g., /login?next=%2Fencounters%2Fmain%23%2Fencounter...)
  // This preserves deep links including hash fragments after login
  const urlParams = new URLSearchParams(window.location.search);
  const nextFromUrl = urlParams.get('next');
  const next = nextFromUrl ? decodeURIComponent(nextFromUrl) : nextProp;

  const [formData, setFormData] = useState<LoginFormData>({
    username: '',
    password: '',
    rememberMe: false,
    twoFactorCode: ''
  });

  const [errors, setErrors] = useState<LoginError[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [requiresEnrollment, setRequiresEnrollment] = useState(false);
  const [enrollmentStep, setEnrollmentStep] = useState<'confirm' | 'verify'>('confirm');
  const [phoneHint, setPhoneHint] = useState<string>('');
  const [statusMessage, setStatusMessage] = useState<string>('');

  const navigate = useNavigate();
  const usernameInputRef = useRef<HTMLInputElement>(null);

  // Determine correct redirect URL based on tenant type
  const getRedirectUrl = () => {
    // Check if this is a single_user (AI client) tenant
    if (actualSingleUser) {
      // AI clients land on the React TECdoc Scribe patient list.
      // Previously this returned '/ai/', which served the AngularJS app and let
      // its ui-router otherwise() rewrite the URL to /ai/#/ai/list.
      // '/ai/list' has no fragment, so Django's url_has_allowed_host_and_scheme
      // still accepts it.
      return '/ai/list';
    }

    // EMR clients use the next parameter (from URL or prop) or default to /
    // IMPORTANT: Hash fragments from AngularJS deep links are preserved by the browser
    // but not included in the 'next' query parameter. The browser treats the hash as
    // belonging to the login page URL itself:
    //   /login?next=/encounters/main#/encounter/active/detail/123/overview
    // So we need to capture window.location.hash and append it to the redirect URL.
    const hashFragment = window.location.hash; // e.g., #/encounter/active/detail/123/overview

    if (hashFragment && next && !next.includes('#')) {
      // Append the hash fragment to preserve the AngularJS deep link
      return next + hashFragment;
    }

    return next;
  };

  // Focus username input on mount (matching AngularJS behavior)
  useEffect(() => {
    if (usernameInputRef.current) {
      usernameInputRef.current.focus();
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEnrollmentSendCode = async () => {
    setErrors([]);
    setStatusMessage('');
    setIsLoading(true);

    try {
      // Send enrollment code using the login enrollment API
      const response = await fetch('/auth/api/login/enroll-2fa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCsrfToken()
        },
        credentials: 'include',
        body: JSON.stringify({
          username: formData.username,
          password: formData.password
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Code sent successfully - move to verification step
        setEnrollmentStep('verify');
        setStatusMessage('A new verification code has been sent to your phone.');
      } else {
        setErrors([{ message: data.error || 'Failed to send verification code' }]);
      }
    } catch (error) {
      setErrors([{ message: 'An error occurred. Please try again.' }]);
      console.error('Enrollment code send error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnrollmentVerify = async () => {
    setErrors([]);
    setStatusMessage('');
    setIsLoading(true);

    try {
      // Verify enrollment code using login enrollment API
      const response = await fetch('/auth/api/login/verify-enrollment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCsrfToken()
        },
        credentials: 'include',
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
          code: formData.twoFactorCode,
          remember_me: formData.rememberMe
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Enrollment complete! Backend has already logged us in
        // Use next parameter from URL, ignore backend redirect (which is always '/')
        const redirectUrl = getRedirectUrl();
        console.log('Enrollment successful, user logged in, redirecting to:', redirectUrl);
        window.location.href = redirectUrl;
      } else {
        setErrors([{ message: data.error || 'Invalid verification code' }]);
      }
    } catch (error) {
      setErrors([{ message: 'An error occurred. Please try again.' }]);
      console.error('Enrollment verification error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginSend2FA = async () => {
    const response = await fetch('/auth/api/login/send-2fa-code', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': getCsrfToken()
      },
      credentials: 'include',
      body: JSON.stringify({
        username: formData.username,
        password: formData.password
      })
    });

    const data = await response.json();

    if (response.ok && data.requires_2fa) {
      setRequires2FA(true);
      setPhoneHint(data.phone_hint || '');
      setStatusMessage('A verification code has been sent to your phone.');
    } else if (data.error) {
      setErrors([{ message: data.error }]);
    }
  };

  const handleResendCode = async (deliveryMethod: 'sms' | 'email' = 'sms') => {
    setErrors([]);
    setStatusMessage('');
    setIsLoading(true);

    try {
      if (deliveryMethod === 'email' && requiresEnrollment) {
        setErrors([{ message: 'Email delivery is available after phone 2FA enrollment is complete.' }]);
        return;
      }

      if (requiresEnrollment && enrollmentStep === 'verify') {
        await handleEnrollmentSendCode();
        setFormData(prev => ({
          ...prev,
          twoFactorCode: ''
        }));
        return;
      }

      const response = await fetch(
        deliveryMethod === 'email'
          ? '/auth/api/login/send-2fa-email-code'
          : '/auth/api/login/send-2fa-code',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken()
          },
          credentials: 'include',
          body: JSON.stringify({
            username: formData.username,
            password: formData.password,
            remember_me: formData.rememberMe
          })
        }
      );

      const data = await response.json();

      if (response.ok && data.requires_2fa) {
        setRequires2FA(true);
        if (data.phone_hint) {
          setPhoneHint(data.phone_hint);
        }
        setFormData(prev => ({
          ...prev,
          twoFactorCode: ''
        }));
        setStatusMessage(
          deliveryMethod === 'email'
            ? `A new verification code has been sent to your primary email${data.email_hint ? ` (${data.email_hint})` : ''}.`
            : 'A new verification code has been sent to your phone.'
        );
      } else {
        setErrors([{ message: data.error || 'Failed to send verification code' }]);
      }
    } catch (error) {
      setErrors([{ message: 'An error occurred while sending a new code. Please try again.' }]);
      console.error('Resend code error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors([]);
    setStatusMessage('');
    setIsLoading(true);

    try {
      if (requiresEnrollment) {
        // Handle enrollment flow
        if (enrollmentStep === 'confirm') {
          // User confirmed, send enrollment code
          await handleEnrollmentSendCode();
          return;
        } else if (enrollmentStep === 'verify') {
          // User entered code, verify it
          await handleEnrollmentVerify();
          return;
        }
      }

      if (!requires2FA) {
        // Step 1: Check credentials and send 2FA code if enabled
        const response = await fetch('/auth/api/login/send-2fa-code', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken()
          },
          credentials: 'include',
          body: JSON.stringify({
            username: formData.username,
            password: formData.password,
            remember_me: formData.rememberMe
          })
        });

        const data = await response.json();

        if (response.ok) {
          if (data.requires_2fa) {
            // User has 2FA enabled - show code input
            setRequires2FA(true);
            setPhoneHint(data.phone_hint || '');
            setStatusMessage('A verification code has been sent to your phone.');
          } else if (data.requires_enrollment) {
            // User needs to enroll in 2FA
            setRequiresEnrollment(true);
            setEnrollmentStep('confirm');
            setPhoneHint(data.phone_hint || '');
          } else if (data.success) {
            // No 2FA - backend has already logged us in (AI clients)
            // Use next parameter from URL, ignore backend redirect (which is always '/')
            const redirectUrl = getRedirectUrl();
            console.log('Login successful (no 2FA), redirecting to:', redirectUrl);
            window.location.href = redirectUrl;
          } else {
            setErrors([{ message: 'Unexpected response from server' }]);
          }
        } else {
          // Handle errors
          if (data.error) {
            setErrors([{ message: data.error }]);
          } else {
            setErrors([{ message: 'Invalid credentials' }]);
          }
        }
      } else {
        // Step 2: Verify 2FA code and complete login
        const response = await fetch('/auth/api/login/verify-2fa-code', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken()
          },
          credentials: 'include',
          body: JSON.stringify({
            username: formData.username,
            password: formData.password,
            code: formData.twoFactorCode,
            remember_me: formData.rememberMe
          })
        });

        const data = await response.json();

        if (response.ok && data.success) {
          // 2FA verified successfully - backend has already created the session
          // Use next parameter from URL, ignore backend redirect (which is always '/')
          const redirectUrl = getRedirectUrl();
          console.log('2FA verification successful, redirecting to:', redirectUrl);
          window.location.href = redirectUrl;
        } else {
          // Handle 2FA verification errors
          if (data.error) {
            setErrors([{ message: data.error }]);
          } else {
            setErrors([{ message: 'Invalid verification code' }]);
          }
        }
      }
    } catch (error) {
      setErrors([{ message: 'An error occurred. Please try again.' }]);
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getCsrfToken = (): string => {
    const cookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrftoken='));
    return cookie ? cookie.split('=')[1] : '';
  };

  const handleNewUser = () => {
    window.location.href = '/auth/accounts/signup';
  };

  const fieldErrors = (fieldName: string) => {
    return errors.filter(e => e.field === fieldName);
  };

  const nonFieldErrors = errors.filter(e => !e.field);

  return (
    <div id="main">
      <div id="values" style={{ textAlign: 'center', top: '100px' }}>
        {actualSingleUser ? (
          <img src="/static/imgs/TECDoc_Scribe_logo.png.webp" alt="TECDoc Scribe" />
        ) : (
          <img src="/static/imgs/tecdoc-logo.png.webp" alt="TECDoc" />
        )}
      </div>

      <div id="auth-column">
        <div id="auth-logo-container">
          {!actualSingleUser && actualLogoUrl && (
            <img id="auth-logo" src={actualLogoUrl} alt="Facility Logo" />
          )}
        </div>

        <div id="auth-form-container">
          <h1>SIGN IN</h1>

          {errors.length > 0 && fieldErrors('username').length === 0 && fieldErrors('password').length === 0 && (
            <p className="errornote">Please correct the errors below.</p>
          )}

          {nonFieldErrors.map((error, index) => (
            <p key={index} className="errornote">
              {error.message}
            </p>
          ))}

          {statusMessage && (
            <p style={{ color: '#2e7d32', marginBottom: '12px' }}>
              {statusMessage}
            </p>
          )}

          <form onSubmit={handleSubmit} id="login-form">
            {!requiresEnrollment && (
              <>
                <div className="field spaced">
                  <label htmlFor="id_username" className="label">Username</label>
                  <input
                    type="text"
                    id="id_username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    ref={usernameInputRef}
                    disabled={isLoading}
                    required
                  />
                  {fieldErrors('username').map((error, index) => (
                    <p key={index} className="errornote">{error.message}</p>
                  ))}
                </div>

                <div className="field spaced">
                  <label htmlFor="id_password" className="label">Password</label>
                  <input
                    type="password"
                    id="id_password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    required
                  />
                  {fieldErrors('password').map((error, index) => (
                    <p key={index} className="errornote">{error.message}</p>
                  ))}
                  <a href="/forgot-password" className="reset-link" tabIndex={2}>
                    Forgot Password?
                  </a>
                </div>
              </>
            )}

            {requiresEnrollment && enrollmentStep === 'confirm' && (
              <div className="enrollment-confirm" style={{ padding: '20px 0' }}>
                <h3 style={{ marginBottom: '15px', fontSize: '18px' }}>Enable Two-Factor Authentication</h3>
                <p style={{ marginBottom: '15px', lineHeight: '1.5' }}>
                  Two-factor authentication is required for all users to protect patient data.
                </p>
                <p style={{ marginBottom: '20px', lineHeight: '1.5' }}>
                  A verification code will be sent to your phone number ending in <strong>****{phoneHint}</strong>.
                </p>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
                  By clicking "Send Code", you agree to receive SMS text messages for authentication purposes.
                </p>
              </div>
            )}

            {requiresEnrollment && enrollmentStep === 'verify' && (
              <div className="field spaced">
                <label htmlFor="id_enrollment_code" className="label">
                  Enter Verification Code
                </label>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                  A 6-digit code has been sent to your phone ending in ****{phoneHint}
                </p>
                <input
                  type="text"
                  id="id_enrollment_code"
                  name="twoFactorCode"
                  value={formData.twoFactorCode}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  required
                  autoComplete="off"
                  maxLength={6}
                  pattern="[0-9]{6}"
                  placeholder="000000"
                  style={{
                    fontSize: '24px',
                    letterSpacing: '8px',
                    textAlign: 'center',
                    fontFamily: 'monospace'
                  }}
                />
                <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
                  Code expires after 5 minutes
                </p>
                <button
                  type="button"
                  onClick={() => handleResendCode('sms')}
                  disabled={isLoading}
                  style={{
                    marginTop: '12px',
                    background: 'none',
                    border: 'none',
                    color: '#d32f2f',
                    cursor: isLoading ? 'default' : 'pointer',
                    padding: 0,
                    textDecoration: 'underline',
                    fontSize: '14px'
                  }}
                >
                  Request a new code
                </button>
              </div>
            )}

            {requires2FA && !requiresEnrollment && (
              <div className="field spaced">
                <label htmlFor="id_two_factor_code" className="label">
                  Verification Code
                </label>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                  A 6-digit code has been sent to your phone ending in ****{phoneHint}
                </p>
                <input
                  type="text"
                  id="id_two_factor_code"
                  name="twoFactorCode"
                  value={formData.twoFactorCode}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  required
                  autoComplete="off"
                  maxLength={6}
                  pattern="[0-9]{6}"
                  placeholder="000000"
                  style={{
                    fontSize: '24px',
                    letterSpacing: '8px',
                    textAlign: 'center',
                    fontFamily: 'monospace'
                  }}
                />
                <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
                  Code expires after 5 minutes
                </p>
                <div style={{ display: 'flex', gap: '14px', marginTop: '12px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => handleResendCode('sms')}
                    disabled={isLoading}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#d32f2f',
                      cursor: isLoading ? 'default' : 'pointer',
                      padding: 0,
                      textDecoration: 'underline',
                      fontSize: '14px'
                    }}
                  >
                    Request a new text code
                  </button>
                  <button
                    type="button"
                    onClick={() => handleResendCode('email')}
                    disabled={isLoading}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#d32f2f',
                      cursor: isLoading ? 'default' : 'pointer',
                      padding: 0,
                      textDecoration: 'underline',
                      fontSize: '14px'
                    }}
                  >
                    Send code by email
                  </button>
                </div>
              </div>
            )}

            {!requiresEnrollment && (
              <div className="field">
                <input
                  type="checkbox"
                  id="id_rememberMe"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                <label htmlFor="id_rememberMe">Keep me logged in</label>
              </div>
            )}

            <div className="submit-row">
              <input
                type="submit"
                value={
                  isLoading
                    ? 'PROCESSING...'
                    : requiresEnrollment && enrollmentStep === 'confirm'
                    ? 'SEND CODE'
                    : requiresEnrollment && enrollmentStep === 'verify'
                    ? 'VERIFY CODE'
                    : requires2FA
                    ? 'VERIFY & SIGN IN'
                    : 'SIGN IN'
                }
                className="auth-button"
                disabled={isLoading}
              />
              {actualSingleUser && !requiresEnrollment && (
                <input
                  type="button"
                  value="NEW USER"
                  className="auth-button"
                  onClick={handleNewUser}
                  disabled={isLoading}
                />
              )}
            </div>
          </form>
        </div>

        <span className="version-number">
          {actualVersion} {actualDeployedEnv}
        </span>
      </div>
    </div>
  );
};

export default LoginPage;
